/** Hosted-page OAuth (SIWE happens on Guardius's /auth/authorize page).
 *  The SPA owns only the public-client legs: PKCE, redirect, code exchange.
 *
 *  Access token lives in sessionStorage (never localStorage; clears on tab
 *  close) so a page refresh doesn't force a full re-auth round-trip.
 *
 *  Ported from penseur-trading's lib/auth.tsx. Deltas: saga has no client-side
 *  write gating (the hosted dashboard is read-only by construction), the
 *  sessionStorage keys are saga-scoped, and the OAuth redirect lands on the
 *  SPA's own base path (BASE_URL = /dashboard/ in the site build). */

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { RuntimeConfig } from "./config";
import {
  buildAuthorizeUrl,
  decodeJwtClaims,
  generatePkce,
  permissionMatches,
  randomState,
  INVITE_CODE_PATTERN,
  PLATFORM_ID,
} from "./oauth";

export interface AuthState {
  config: RuntimeConfig;
  token: string | null;
  address: string | null;
  signIn(): Promise<void>;
  signOut(): void;
}

/** What the signed-in identity may do in the studio, derived from the token's
 *  `permissions[]` for UI gating only. `canAuthor` (holds `definition:*:write`)
 *  and `canRun` (`run:*:execute`) gate the writable studio; `canInvite`
 *  (`member:*:invite`) gates minting member invites + viewing the roster;
 *  `canManageTeam` (`team:*:admin`) additionally gates listing/revoking invites.
 *  A reader (only `dashboard:*:read`) gets all four false. The snapshot API's
 *  Guardius authorizer remains the authoritative enforcement point. */
export interface Capability {
  canAuthor: boolean;
  canRun: boolean;
  canManageTeam: boolean;
  canInvite: boolean;
}

/** The result of a single-invite redemption attempt — either threaded back from
 *  the token exchange (Guardius redeems server-side after sign-in) or returned
 *  by the explicit `redeemInvite` call. `reason` carries the Guardius failure
 *  `code` (invalid_invite | invite_expired | invite_revoked | invite_exhausted)
 *  so the UI can render a specific message via `friendlyInviteError`. */
export interface InviteOutcome {
  status: "redeemed" | "already_redeemed" | "failed";
  reason?: string;
  grantedRoleIds?: string[];
}

/** Extended internal surface: the provider also exposes a callback error so
 *  the sign-in screen can render it, plus the captured/pending invite code and
 *  the latest redemption outcome. */
export interface AuthContextValue extends AuthState {
  error: string | null;
  pendingInvite: string | null;
  inviteOutcome: InviteOutcome | null;
  redeemInvite(code: string): Promise<InviteOutcome>;
  /** The raw permission strings from the current token (empty when signed out). */
  permissions: string[];
  /** Studio capability derived from {@link permissions} for UI gating. */
  capability: Capability;
}

/** Map a Guardius invite-failure `reason` to a user-facing message. Shared by
 *  the boot/exchange paths here and Task 3's sign-in UI. Any unknown or absent
 *  reason falls back to the generic invalid-invite copy. */
export function friendlyInviteError(reason?: string): string {
  switch (reason) {
    case "invite_expired":
      return "This invite has expired.";
    case "invite_revoked":
      return "This invite was revoked.";
    case "invite_exhausted":
      return "This invite has no uses left.";
    case "invalid_invite":
    default:
      return "This invite link isn't valid — check the link or ask for a new one.";
  }
}

// sessionStorage keys. `saga.oauth.pending` carries the one-shot PKCE
// verifier + CSRF state + redirect URI, written by signIn() and consumed
// exactly once by the boot callback. `saga.token` holds the access token.
const PENDING_KEY = "saga.oauth.pending";
export const TOKEN_KEY = "saga.token";
// Holds a captured invite code across the hosted-page round-trip (and across a
// refresh mid-flow). The authoritative redemption is server-side; this stash is
// what `signIn()` threads into the authorize URL.
const INVITE_KEY = "saga.invite";

interface PendingAuth {
  verifier: string;
  state: string;
  redirectUri: string;
  invite?: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Derive UI fields from a stored token. Returns nulls when the token is
 *  absent, malformed, or expired (expired tokens are cleared by the caller). */
function deriveState(token: string | null): { address: string | null; expired: boolean } {
  if (!token) return { address: null, expired: false };
  const claims = decodeJwtClaims(token);
  if (!claims) return { address: null, expired: true };
  if (typeof claims.exp === "number" && claims.exp * 1000 <= Date.now()) {
    return { address: null, expired: true };
  }
  return { address: claims.wallet_address ?? claims.sub, expired: false };
}

/** The raw permission strings carried by a token's claims (empty when the token
 *  is absent or malformed). UI gating only — no signature verification. */
function derivePermissions(token: string | null): string[] {
  if (!token) return [];
  return decodeJwtClaims(token)?.permissions ?? [];
}

/** Derive the studio {@link Capability} from a token's permissions against the
 *  saga platform + configured tenant. `canAuthor` requires `definition:*:write`,
 *  `canRun` requires `run:*:execute`; both false when signed out. */
function deriveCapability(token: string | null, config: RuntimeConfig): Capability {
  if (!token) return { canAuthor: false, canRun: false, canManageTeam: false, canInvite: false };
  const perms = decodeJwtClaims(token)?.permissions;
  const t = config.tenantId;
  return {
    canAuthor: permissionMatches(perms, `*:${PLATFORM_ID}:${t}:definition:*:write`),
    canRun: permissionMatches(perms, `*:${PLATFORM_ID}:${t}:run:*:execute`),
    canManageTeam: permissionMatches(perms, `*:${PLATFORM_ID}:${t}:team:*:admin`),
    canInvite: permissionMatches(perms, `*:${PLATFORM_ID}:${t}:member:*:invite`),
  };
}

export function AuthProvider({
  config,
  children,
}: {
  config: RuntimeConfig;
  children: ReactNode;
}) {
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingInvite, setPendingInvite] = useState<string | null>(null);
  const [inviteOutcome, setInviteOutcome] = useState<InviteOutcome | null>(null);

  // One-shot guard for the OAuth callback exchange. Under React StrictMode the
  // boot effect is invoked twice; the first invoke consumes the one-shot pending
  // stash and starts the code exchange, so the second invoke MUST NOT re-enter
  // the callback path (the stash is gone — re-entry would spuriously reject as a
  // state mismatch and clobber a successful sign-in). Because the stash is
  // single-use, a second exchange is impossible, so the resolved token is
  // committed to state regardless of the effect-cleanup `cancelled` flag.
  const exchangeStarted = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");

      // Invite capture runs BEFORE the callback branch so a `?invite=...` link
      // (which carries no code/state) is stashed for the eventual sign-in. A
      // malformed code is surfaced but never stashed. URL stripping is gated on
      // an invite/error param being present so plain loads stay untouched.
      const invite = params.get("invite");
      if (invite !== null) {
        stripQuery();
        if (INVITE_CODE_PATTERN.test(invite)) {
          sessionStorage.setItem(INVITE_KEY, invite);
          if (!cancelled) setPendingInvite(invite);
        } else if (!cancelled) {
          setInviteOutcome({ status: "failed", reason: "invalid_invite" });
        }
      } else if (!cancelled) {
        // Refresh mid-flow: restore a previously captured invite from the stash.
        const stashed = sessionStorage.getItem(INVITE_KEY);
        if (stashed) setPendingInvite(stashed);
      }

      // OAuth error bounce-back: Guardius redirects here with `?error=...` (and
      // no code) when pre-auth invite validation or sign-in itself fails.
      const oauthError = params.get("error");
      if (oauthError && !code) {
        stripQuery();
        if (oauthError === "invalid_invite") {
          sessionStorage.removeItem(INVITE_KEY);
          if (!cancelled) {
            setPendingInvite(null);
            setInviteOutcome({ status: "failed", reason: "invalid_invite" });
          }
        } else if (!cancelled) {
          setError("Sign-in failed: " + oauthError);
        }
        return;
      }

      if (code && state) {
        if (exchangeStarted.current) return;
        exchangeStarted.current = true;

        // Read-and-remove the one-shot pending stash. A missing stash or a
        // state mismatch is a hard reject — we must NOT exchange the code.
        const raw = sessionStorage.getItem(PENDING_KEY);
        sessionStorage.removeItem(PENDING_KEY);
        stripQuery();

        let pending: PendingAuth | null = null;
        try {
          pending = raw ? (JSON.parse(raw) as PendingAuth) : null;
        } catch {
          pending = null;
        }

        if (!pending || pending.state !== state) {
          setError("Sign-in could not be verified (state mismatch). Please try again.");
          return;
        }

        try {
          const res = await fetch(`${config.guardiusUrl}/auth/token`, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              grant_type: "authorization_code",
              code,
              redirect_uri: pending.redirectUri,
              code_verifier: pending.verifier,
              platform_id: PLATFORM_ID,
            }),
          });
          if (!res.ok) {
            setError(`Token exchange failed (${res.status}).`);
            return;
          }
          const body = (await res.json()) as { access_token?: string; invite?: InviteOutcome };
          if (!body.access_token) {
            setError("Token exchange returned no access token.");
            return;
          }
          // No auto-refresh: on expiry the user re-signs in, which the hosted
          // page's session typically makes a one-click action.
          sessionStorage.setItem(TOKEN_KEY, body.access_token);
          setToken(body.access_token);
          // The invite (if any) is redeemed server-side before the code is
          // minted; the token commit above is unconditional so a failed
          // redemption never blocks login. Clear the stash in all cases — the
          // round-trip is over (success → done; failure → fresh link / retry).
          if (body.invite) setInviteOutcome(body.invite);
          sessionStorage.removeItem(INVITE_KEY);
          setPendingInvite(null);
        } catch (e) {
          setError(e instanceof Error ? e.message : "Token exchange failed.");
        }
        return;
      }

      // No callback: load any persisted token, clearing it if expired.
      const stored = sessionStorage.getItem(TOKEN_KEY);
      if (stored) {
        const { expired } = deriveState(stored);
        if (expired) {
          sessionStorage.removeItem(TOKEN_KEY);
          if (!cancelled) setToken(null);
        } else if (!cancelled) {
          setToken(stored);
        }
      }
    };

    void boot();
    return () => {
      cancelled = true;
    };
  }, [config]);

  const signIn = async (): Promise<void> => {
    setError(null);
    const { verifier, challenge } = await generatePkce();
    const state = randomState();
    // The SPA lives at BASE_URL (/dashboard/ in the site build; / is the
    // landing page), so the OAuth callback must return here, not to the
    // landing. Guardius matches the bare origin for the allowed-origin check.
    const redirectUri = window.location.origin + import.meta.env.BASE_URL;
    // Read the captured invite from the stash (pendingInvite state mirrors it).
    // Keeping it in PendingAuth is belt-and-suspenders for debugging — the
    // authoritative redemption happens server-side on /auth/authorize.
    const invite = sessionStorage.getItem(INVITE_KEY) ?? undefined;
    const pending: PendingAuth = { verifier, state, redirectUri, invite };
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    window.location.assign(buildAuthorizeUrl(config, { challenge, state, redirectUri, invite }));
  };

  const redeemInvite = async (code: string): Promise<InviteOutcome> => {
    if (!token) return { status: "failed", reason: "invalid_invite" };
    try {
      const res = await fetch(`${config.guardiusUrl}/invites/redeem`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify({ code }),
      });
      const body = (await res.json()) as { status?: string; code?: string; grantedRoleIds?: string[] };
      const outcome: InviteOutcome = res.ok
        ? { status: body.status as InviteOutcome["status"], grantedRoleIds: body.grantedRoleIds }
        : { status: "failed", reason: body.code ?? "invalid_invite" };
      setInviteOutcome(outcome);
      // On success the CURRENT token does NOT carry the newly granted
      // permissions — saga has no refresh flow, so callers prompt a re-sign-in.
      if (outcome.status !== "failed") sessionStorage.removeItem(INVITE_KEY);
      return outcome;
    } catch {
      const outcome: InviteOutcome = { status: "failed", reason: "invalid_invite" };
      setInviteOutcome(outcome);
      return outcome;
    }
  };

  const signOut = (): void => {
    sessionStorage.removeItem(PENDING_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(INVITE_KEY);
    setToken(null);
    setError(null);
    setPendingInvite(null);
    setInviteOutcome(null);
  };

  const { address } = deriveState(token);
  const permissions = derivePermissions(token);
  const capability = deriveCapability(token, config);

  const value: AuthContextValue = {
    config,
    token,
    address,
    error,
    pendingInvite,
    inviteOutcome,
    signIn,
    signOut,
    redeemInvite,
    permissions,
    capability,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}

/** Strip the OAuth callback query (code/state) from the URL without a reload. */
function stripQuery(): void {
  window.history.replaceState({}, "", window.location.pathname + window.location.hash);
}
